import React, {FC} from 'react';

import download from 'assets/v3/guide/image/download.webp';
import engineConfig from 'assets/v3/guide/image/engine-config.webp';
import refine from 'assets/v3/guide/image/refine.webp';
import refineDone from 'assets/v3/guide/image/refine-done.webp';
import result from 'assets/v3/guide/image/result.webp';
import translating from 'assets/v3/guide/image/translating.webp';
import uploadEcommerce1 from 'assets/v3/guide/image/upload-ecommerce-1.webp';
import uploadEcommerce2 from 'assets/v3/guide/image/upload-ecommerce-2.webp';
import uploadLink from 'assets/v3/guide/image/upload-link.webp';
import uploadLocal from 'assets/v3/guide/image/upload-local.webp';

/**
 * Русский текст вкладки «Перевод изображений» руководства пользователя (структура элементов 1:1 с en.tsx).
 * AI-перевод, 2026-09-17.
 */
const RuContent: FC = () => {
  return (
    <>
      <h2>1. Перед загрузкой</h2>
      <p>Перед загрузкой при необходимости выберите версию, исходный язык и язык перевода.</p>
      <img
        alt="Настройки перевода: версия, исходный язык и язык перевода"
        decoding="async"
        loading="lazy"
        src={engineConfig}
      />
      <p>
        <strong>Исходный язык</strong>: выберите язык текста на исходном изображении.
      </p>
      <p>
        <strong>Язык перевода</strong>: язык, на который вы хотите перевести.
      </p>

      <h3>Какой переводчик (движок) выбрать?</h3>
      <div className="v3-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Переводчик</th>
              <th>Сильные стороны</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Alibaba Cloud / Baidu</td>
              <td>
                Более идиоматичный китайский перевод, лучше всего подходит для перевода иностранных языков на китайский;
                при переводе с китайского на иностранные языки результаты немного слабее, зато дешевле.
              </td>
            </tr>
            <tr>
              <td>Google</td>
              <td>Лучше подходит для перевода на иностранные языки, охватывает широкий набор языков.</td>
            </tr>
            <tr>
              <td>Papago</td>
              <td>
                Продукт корейского облачного провайдера Naver — самый идиоматичный выбор для перевода на корейский язык.
              </td>
            </tr>
            <tr>
              <td>DeepL</td>
              <td>
                Лучше подходит для перевода на иностранные языки с широким охватом (японский, английский, немецкий,
                русский и др.), отлично справляется со сленгом, диалектами и классическими оборотами.
              </td>
            </tr>
            <tr>
              <td>ChatGPT</td>
              <td>Ведущая сегодня модель обработки естественного языка — точный и идиоматичный перевод текста.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>2. Загрузка изображений</h2>
      <h3>Загрузите локальные изображения</h3>
      <p>Нажмите кнопку загрузки, откройте локальные файлы и выберите нужные изображения товара.</p>
      <img alt="Загрузка локальных файлов" decoding="async" loading="lazy" src={uploadLocal} />

      <h3>Введите ссылки на изображения</h3>
      <p>
        Перейдите на вкладку ссылок на изображения, чтобы пакетно импортировать URL-адреса изображений, которые нужно
        перевести.
      </p>
      <img alt="Ввод прямых ссылок" decoding="async" loading="lazy" src={uploadLink} />

      <h3>Вставьте ссылку на товар из интернет-магазина</h3>
      <p>
        Вставьте ссылку на товар, чтобы автоматически собрать его изображения, затем выберите те, которые нужно
        перевести.
      </p>
      <img alt="Вставьте ссылку на товар из интернет-магазина" decoding="async" loading="lazy" src={uploadEcommerce1} />
      <img alt="Собранные снимки товара" decoding="async" loading="lazy" src={uploadEcommerce2} />

      <h2>3. Перевод изображений</h2>
      <p>После загрузки нажмите кнопку 【Перевести сейчас】, и ИИ автоматически начнёт перевод.</p>
      <img alt="Запуск перевода" decoding="async" loading="lazy" src={translating} />
      <p>
        Перевод изображений с помощью ИИ занимает около 2–3 секунд. По завершении сравните исходное и переведённое
        изображения рядом. Если результат устраивает, при необходимости нажмите 【Скачать все】 или 【Скопировать ссылку
        на переведённое изображение】; если нет или вы хотите переделать вёрстку, нажмите 【Ретушь】, чтобы открыть
        инструмент ретуши и подправить изображение.
      </p>
      <img alt="Сравнение исходного и переведённого вариантов" decoding="async" loading="lazy" src={result} />

      <h2>4. Ретушь изображений</h2>
      <p>
        Нажмите 【Ретушь】, чтобы открыть рабочую область ретуши, затем щёлкните текст, который хотите отредактировать.
      </p>
      <ul>
        <li>
          <strong>Область редактирования текста</strong>: отредактируйте исходный текст и переведите его заново;
          настройте стиль текста, угол наклона, обводку и многое другое.
        </li>
        <li>
          <strong>Инструменты восстановления</strong>: добавьте текст для перевода или воспользуйтесь умным ластиком,
          чтобы удалить или исправить дефекты.
        </li>
      </ul>
      <img alt="Рабочая область ретуши" decoding="async" loading="lazy" src={refine} />
      <p>
        По завершении нажмите 【Завершить редактирование】, чтобы сохранить изменения и выйти из инструмента ретуши.
      </p>
      <img alt="Завершение ретуши" decoding="async" loading="lazy" src={refineDone} />

      <h2>5. Скачивание</h2>
      <p>
        Нажмите 【Скачать все】 или 【Скопировать ссылку на изображение】. Отретушированные изображения автоматически
        заменяют неотретушированные при скачивании или экспорте.
      </p>
      <img alt="Скачивание переведённых результатов" decoding="async" loading="lazy" src={download} />
    </>
  );
};

export default RuContent;
